import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-600" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>
              AICreditPilot
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Terms of Service
        </h1>
        <p className="text-slate-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              By accessing or using AICreditPilot ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
            </p>
            <p className="text-slate-600">
              We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-4">
              AICreditPilot provides credit repair assistance services, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Analysis of credit reports for potential errors and inaccuracies</li>
              <li>Generation of dispute letters to credit bureaus</li>
              <li>Mailing of dispute letters on your behalf (Premium plan)</li>
              <li>Credit education and coaching</li>
              <li>Credit score tracking tools</li>
            </ul>
            <p className="text-slate-600 mt-4">
              <strong>Important:</strong> We are not a law firm and do not provide legal advice. We provide credit repair services to assist you in disputing inaccurate information on your credit reports.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Eligibility</h2>
            <p className="text-slate-600">
              You must be at least 18 years old and a legal resident of the United States to use our Service. By using the Service, you represent and warrant that you meet these eligibility requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Account Registration</h2>
            <p className="text-slate-600 mb-4">To use certain features of the Service, you must:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Create an account with accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Authorization</h2>
            <p className="text-slate-600 mb-4">
              By using our dispute services, you authorize AICreditPilot to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Access your credit report information that you provide</li>
              <li>Prepare and send dispute letters to credit bureaus on your behalf</li>
              <li>Communicate with credit bureaus regarding your disputes</li>
              <li>Act as your authorized representative for credit dispute purposes</li>
            </ul>
            <p className="text-slate-600 mt-4">
              This authorization can be revoked at any time by contacting us or closing your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Subscription Plans and Payments</h2>
            <h3 className="text-lg font-medium text-slate-800 mb-2">Free Plan</h3>
            <p className="text-slate-600 mb-4">
              Includes basic credit insights, educational resources, and manual letter downloads at no cost.
            </p>
            <h3 className="text-lg font-medium text-slate-800 mb-2">Premium Plan ($29.99/month)</h3>
            <p className="text-slate-600 mb-4">
              Includes up to 5 dispute letters per month, automatic mailing, AI Credit Coach, and priority support.
            </p>
            <h3 className="text-lg font-medium text-slate-800 mb-2">Billing</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Premium subscriptions are billed monthly</li>
              <li>Payment is due at the start of each billing cycle</li>
              <li>All payments are processed securely through Stripe</li>
              <li>Prices are subject to change with 30 days notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Cancellation and Refunds</h2>
            <p className="text-slate-600 mb-4">
              You may cancel your subscription at any time through your account settings. Upon cancellation:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Your subscription will remain active until the end of the current billing period</li>
              <li>No refunds are provided for partial months</li>
              <li>You will retain access to Free plan features after cancellation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. No Guarantee of Results</h2>
            <p className="text-slate-600 mb-4">
              <strong>Important Disclaimer:</strong> While we strive to help improve your credit situation, we cannot guarantee specific results. Credit repair outcomes depend on many factors, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>The accuracy of information you provide</li>
              <li>The nature and validity of disputed items</li>
              <li>Credit bureau response times and decisions</li>
              <li>Your overall credit history and behavior</li>
            </ul>
            <p className="text-slate-600 mt-4">
              We do not guarantee any specific credit score increase or that all disputed items will be removed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Prohibited Uses</h2>
            <p className="text-slate-600 mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Provide false or misleading information</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the proper functioning of the Service</li>
              <li>Resell or redistribute the Service without permission</li>
              <li>Dispute items you know to be accurate</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Intellectual Property</h2>
            <p className="text-slate-600">
              All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are owned by AICreditPilot and are protected by copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AICREDITPILOT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Loss of profits or revenue</li>
              <li>Loss of data</li>
              <li>Credit score changes or outcomes</li>
              <li>Decisions made by credit bureaus</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Indemnification</h2>
            <p className="text-slate-600">
              You agree to indemnify and hold harmless AICreditPilot and its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">13. Dispute Resolution</h2>
            <p className="text-slate-600 mb-4">
              Any disputes arising from these Terms or the Service shall be resolved through:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>First, good faith negotiation between the parties</li>
              <li>If unresolved, binding arbitration in accordance with the rules of the American Arbitration Association</li>
            </ul>
            <p className="text-slate-600 mt-4">
              You agree to waive any right to a jury trial or to participate in a class action lawsuit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">14. Governing Law</h2>
            <p className="text-slate-600">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">15. Contact Information</h2>
            <p className="text-slate-600 mb-4">
              For questions about these Terms, please contact us:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li><strong>Email:</strong> support@aicreditpilot.com</li>
              <li><strong>Phone:</strong> 1-800-697-4877</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-slate-500 text-sm">
          © 2026 AICreditPilot. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
